import { useCallback, useEffect, useState } from "react";

// Custom in-tab broadcast — `storage` events only fire ACROSS tabs, not
// within the same one, so multiple useNavCollapse instances on a single
// page would otherwise drift out of sync after a toggle. AppShell + the
// OperatorRail both call this hook with the same key; they need to update
// together so the IonMenu wrapper width tracks the rail's collapse state.
const COLLAPSE_EVENT = "rr-nav-collapse-change";

interface CollapseChangeDetail {
  storageKey: string;
  collapsed: boolean;
}

/**
 * Persists rail collapse state across reloads. Scoped by key so the
 * customer CRM and operator console can keep independent preferences
 * if you ever expose a collapse on the customer side too.
 *
 * Broadcasts changes within the tab via a CustomEvent so sibling
 * subscribers (e.g. AppShell + OperatorRail) stay in sync without
 * lifting state up through the shell.
 */
export function useNavCollapse(storageKey = "rr.nav.collapsed", initial = false) {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw === null ? initial : raw === "1";
    } catch {
      return initial;
    }
  });

  // Listen for in-tab broadcasts from sibling instances with the same key.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<CollapseChangeDetail>).detail;
      if (!detail || detail.storageKey !== storageKey) return;
      setCollapsedState(detail.collapsed);
    };
    window.addEventListener(COLLAPSE_EVENT, onChange);
    return () => window.removeEventListener(COLLAPSE_EVENT, onChange);
  }, [storageKey]);

  const setCollapsed = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setCollapsedState((prev) => {
        const resolved = typeof next === "function" ? (next as (p: boolean) => boolean)(prev) : next;
        try {
          window.localStorage.setItem(storageKey, resolved ? "1" : "0");
        } catch {
          /* ignore quota / privacy mode */
        }
        try {
          window.dispatchEvent(
            new CustomEvent<CollapseChangeDetail>(COLLAPSE_EVENT, {
              detail: { storageKey, collapsed: resolved },
            }),
          );
        } catch {
          /* CustomEvent should always be available in evergreen browsers */
        }
        return resolved;
      });
    },
    [storageKey],
  );

  const toggle = useCallback(() => setCollapsed((c) => !c), [setCollapsed]);
  return { collapsed, setCollapsed, toggle };
}
